import { Trans } from "react-i18next";
import { Phone, ReceiptText, X } from "lucide-react";

// Shown instead of the guide when the machine didn't start after a successful
// payment. We can't tell why (door left open, machine fault…), so the message
// stays generic. The user can't fix this on the kiosk, so we only give them what
// support needs: the session id, which is also printed on their receipt as
// "Blokk sorszám".

export function FailedState({
  machineNumber,
  sessionId,
  supportPhone,
  returnAfterSeconds,
  onFinish,
}: {
  machineNumber: number;
  sessionId: string;
  supportPhone: string;
  returnAfterSeconds: number;
  onFinish?: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center text-center mt-8 animate-[guide-rise_600ms_ease-out]">
      <span className="size-28 rounded-full bg-bub-orange flex items-center justify-center shadow-lg animate-[guide-pop_500ms_ease-out_150ms_both]">
        <X className="size-16" stroke="white" strokeWidth={3} />
      </span>
      <p className="text-[52px] font-fancy font-bold leading-tight mt-6">
        <Trans values={{ machineNumber }}>
          A(z) {{ machineNumber }}. gép nem indult el
        </Trans>
      </p>
      <p className="text-[30px] mt-3 text-bub-dark-blue/80 max-w-[860px]">
        <Trans>Valami hiba történt, ezért a gép nem tudott elindulni.</Trans>
      </p>
      <p className="text-[30px] mt-1 font-bold">
        <Trans>A fizetésedet rögzítettük, ügyfélszolgálatunk segít!</Trans>
      </p>

      <SessionIdCard sessionId={sessionId} />

      <div className="mt-6 w-full flex items-center gap-6 rounded-3xl bg-bub-vivid-blue/10 px-10 py-7 text-left">
        <span className="size-20 shrink-0 rounded-full bg-bub-vivid-blue flex items-center justify-center">
          <Phone className="size-10" stroke="white" />
        </span>
        <div>
          <p className="text-[28px]">
            <Trans>Hívd 0-24-ben elérhető ügyfélszolgálatunkat:</Trans>
          </p>
          <p className="text-[52px] font-fancy font-bold leading-tight">
            {supportPhone}
          </p>
        </div>
      </div>

      <div className="mt-auto w-full rounded-3xl bg-white shadow-lg px-10 pt-6 pb-5">
        <button
          onClick={onFinish}
          className="rounded-full border-4 border-bub-dark-blue/30 px-12 pt-5 pb-4 text-[36px] font-fancy font-bold active:scale-95 transition-transform"
        >
          <Trans>Vissza a kezdőképernyőre</Trans>
        </button>
        <div className="mt-6 h-2 rounded-full bg-bub-dark-blue/10 overflow-hidden">
          <div
            className="h-full bg-bub-dark-blue/40 origin-left"
            style={{
              animation: `guide-countdown ${returnAfterSeconds}s linear both`,
            }}
            onAnimationEnd={onFinish}
          />
        </div>
        <p className="text-[20px] mt-2 text-bub-dark-blue/60">
          <Trans>
            A kioszk hamarosan visszaáll a kezdőképernyőre. Előtte fotózd le
            a sorszámot, vagy tedd el a nyugtát!
          </Trans>
        </p>
      </div>
    </div>
  );
}

// the id next to a little receipt, so they know where to find it later
function SessionIdCard({ sessionId }: { sessionId: string }) {
  return (
    <div className="mt-8 w-full flex items-center gap-8 rounded-3xl bg-white shadow-lg px-10 py-7 text-left">
      <MiniReceipt sessionId={sessionId} />
      <div className="min-w-0">
        <p className="text-[28px] font-bold">
          <Trans>Blokk sorszám</Trans>
        </p>
        <p className="text-[64px] font-bold leading-tight tracking-wider tabular-nums break-all">
          {sessionId}
        </p>
        <p className="text-[24px] text-bub-dark-blue/70 flex items-center gap-2">
          <ReceiptText className="size-7 shrink-0" stroke="#005D86" />
          <Trans>Ezt a számot a nyugtádon is megtalálod.</Trans>
        </p>
      </div>
    </div>
  );
}

function MiniReceipt({ sessionId }: { sessionId: string }) {
  return (
    <svg
      viewBox="0 0 120 160"
      className="w-[120px] shrink-0 drop-shadow-md"
      aria-hidden
    >
      {/* paper with a torn bottom edge */}
      <path
        d="M8 0h104v148l-8 8-8-8-8 8-8-8-8 8-8-8-8 8-8-8-8 8-8-8-8 8-8-8-8 8-8-8z"
        fill="white"
        stroke="#005D86"
        strokeOpacity=".25"
        strokeWidth="2"
      />
      <text x="60" y="20" textAnchor="middle" fontSize="13" fontWeight="bold" fill="#005D86">
        BUBBLES
      </text>
      <rect x="22" y="30" width="76" height="4" rx="2" fill="#005D86" opacity=".2" />
      {/* the line they're looking for */}
      <rect x="10" y="42" width="100" height="34" rx="6" fill="#f45e22" opacity=".15" />
      <text x="60" y="55" textAnchor="middle" fontSize="9" fill="#005D86">
        Blokk sorszam:
      </text>
      <text x="60" y="69" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#005D86">
        {sessionId}
      </text>
      {[86, 98, 110, 122].map((y, index) => (
        <rect
          key={y}
          x="16"
          y={y}
          width={index % 2 ? 60 : 88}
          height="4"
          rx="2"
          fill="#005D86"
          opacity=".2"
        />
      ))}
    </svg>
  );
}
